import '../../../ui-styles/ibest-style.css';
import { useCallback, useMemo, useState } from 'react';
import { PrintChartImageGrid, RichSummaryReport, defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useLivePlotPrintImages, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyzeRequest } from './api';
import type { ChartKey, ResultSummary, SDOFSolverResultState } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, ORIENTATION_TYPES, PRINT_CHART_ASPECT, PRINT_CHART_IMAGE_OPTIONS, PRINT_CHART_ORDER } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, GenerateLinkButton, LivePlot } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

function ResultSummaryPanel({ summary }: { summary?: ResultSummary }) {
  if (!summary) {
    return <div className="p-4 text-black-500 italic">No analysis results yet.</div>;
  }

  return (
    <div className="rounded-lg border bg-white p-4">
      <RichSummaryReport summary={summary} className="text-[13px] leading-5 text-slate-800" />
    </div>
  );
}

export default function App() {
  // Check for calcId first (short URL from CalcPad)
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  // Merge URL parameters with default values
  const initialValues = useMemo<FormValues>(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'iSDOF Solver',
          length: '10',
          tributaryWidth: '5',
          hingeDistance: '5.0',
          weight: '10*5*12',
          orientation: 0,
          klm: '(0.78+0.66)/2',
          damping: '0.02',
          resistanceNL: '-9.0, -9.0, 0, 9.0, 9.0',
          deflectionNL: '-2.0, -1.0, 0, 1.0, 2.0',
          resistanceLinear: '-9.0, 0, 9.0',
          deflectionLinear: '-1.0, 0, 1.0',
          pressureHistory: '4, 0,0',
          timeHistory: '0, 14, 30',
          u0: '0.0',
          v0: '0.0'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({
    schema,
    defaultValues: initialValues
  });

  const values = watch();

  const { saveDataToDB } = useAppStorage<FormValues, SDOFSolverResultState>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, handleAnalyze } = useAnalysisWorkflow<FormValues, ParsedValues, SDOFSolverResultState>({
    errors,
    values,
    schema: { parse: formValues => schema.parse({ ...formValues }) },
    analyze: analyzeRequest,
    saveDataToDB
  });
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const { chartRefs, printRootRef, buildChartImages } = useLivePlotPrintImages<ChartKey, SDOFSolverResultState>({
    analysisResult,
    setAnalysisResult,
    getImageOptions: key => PRINT_CHART_IMAGE_OPTIONS[PRINT_CHART_ASPECT[key]],
    errorMessage: 'Unable to prepare charts for printing. Please wait for the charts to finish rendering and try again.'
  });
  const handleSave = useCallback(async () => {
    if (Object.keys(errors).length > 0) {
      console.warn('Save blocked: fix errors first', errors);
      return;
    }

    setSaveStatus('saving');
    try {
      let resultToSave = analysisResult;

      if (analysisResult?.plotData) {
        const charts = await buildChartImages(analysisResult);
        resultToSave = { ...analysisResult, charts };
        setAnalysisResult(resultToSave);
      }

      await saveDataToDB(values.project, values.title, values, resultToSave ?? undefined);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Save Error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  }, [analysisResult, buildChartImages, errors, saveDataToDB, setAnalysisResult, values]);

  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, SDOFSolverResultState>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  /* prettier-ignore */
  const fields = defineFields<FormValues>([
    {kind: 'input', name: 'title', label: 'Title', helpText: 'Enter the title of the component', placeholder: 'Component Title' },
    {kind: 'input', name: 'length', label: 'Length', unit: 'ft', helpText: 'Enter the length of the component', placeholder: 'Component Length' },
    {kind: 'input', name: 'tributaryWidth', label: 'Tributary Width', unit: 'ft', helpText: 'Enter the tributary width of the component', placeholder: 'Component Tributary Width'},
    {kind: 'input', name: 'hingeDistance', label: 'Hinge Distance', unit: 'ft', helpText: 'Enter the hinge distance of the component', placeholder: 'Component Hinge Distance'},
    {kind: 'input', name: 'weight', label: 'Total Weight', unit: 'lbs', helpText: 'Enter the total weight on the component', placeholder: 'Component Total Weight'},
    {kind: 'input', name: 'klm', label: 'KLM', unit: '', helpText: 'Enter the KLM value', placeholder: 'KLM'},

    {kind: 'dropdown', name: 'orientation', label: 'Orientation', options: ORIENTATION_TYPES, helpText: 'Vertical has gravity load...', placeholder: 'Component Orientation'},
    
    { kind: 'input', name: 'resistanceNL', label: 'Resistance NL', unit: 'kips', helpText: 'Resistance NL', placeholder: 'Resistance NL' },
    { kind: 'input', name: 'deflectionNL', label: 'Deflection NL', unit: 'in', helpText: 'Deflection NL', placeholder: 'Deflection NL' },
    { kind: 'input', name: 'resistanceLinear', label: 'Resistance Linear', unit: 'kips', helpText: 'Resistance Linear', placeholder: 'Resistance Linear' },
    { kind: 'input', name: 'deflectionLinear', label: 'Deflection Linear', unit: 'in', helpText: 'Deflection Linear', placeholder: 'Deflection Linear' },

    { kind: 'input', name: 'pressureHistory', label: 'Pressure History', unit: 'psi', helpText: 'Pressure History', placeholder: 'Pressure History' },
    { kind: 'input', name: 'timeHistory', label: 'Time History', unit: 'msec', helpText: 'Time History', placeholder: 'Time History' },
    { kind: 'input', name: 'u0', label: 'Initial Displacement', unit: 'in', helpText: 'Initial Displacement', placeholder: 'Initial Displacement' },
    { kind: 'input', name: 'v0', label: 'Initial Velocity', unit: 'in/sec', helpText: 'Initial Velocity', placeholder: 'Initial Velocity' }
  ]);

  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">Explicit General SDOF Solver</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-2 sm:p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex w-full flex-col gap-2 mt-2 sm:mt-1 sm:flex-row sm:flex-wrap">
            <button className="submit-button w-full sm:w-auto sm:flex-1" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button w-full sm:w-auto sm:flex-1" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} className="w-full sm:w-auto" />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid min-w-0 grid-rows-[auto_1fr] rounded-xl gap-y-0 w-full">
          <div className="analysis-app__results shadow-div rounded-xl m-1">
            <ResultSummaryPanel summary={analysisResult?.summary} />
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.plotData && (
              <div className="analysis-app__charts-grid grid grid-cols-1 xl:grid-cols-2 items-stretch gap-3 m-2 sm:m-4 print:gap-1">
                {Object.entries(analysisResult.plotData)
                  .filter(([, plot]) => !!plot)
                  .map(([key, plot]) => {
                    const chartKey = key as ChartKey;

                    return (
                      <div key={key} className="analysis-app__chart-card min-w-0 break-inside-avoid">
                        <LivePlot
                          ref={node => {
                            chartRefs.current[chartKey] = node;
                          }}
                          plot={plot}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div ref={printRootRef} className="print-root">
          <div className="print-a4-landscape box-border bg-white p-[1mm]">
            <div className="grid h-full min-h-0 grid-cols-[24%_76%] border border-black bg-white text-black">
              <div className="flex h-full min-h-0 flex-col">
                <div className="p-1">
                  <div className="grid grid-cols-[auto_1fr] text-[10px] m-0 p-0 leading-none mb-1 gap-x-1">
                    <div className="font-semibold">Project : </div>
                    <div>{values.project || '—'}</div>
                  </div>
                  <div className="grid grid-cols-[auto_1fr] text-[10px] m-0 p-0 leading-none gap-x-1">
                    <div className="font-semibold">Title : </div>
                    <div>{values.title || 'iSDOF Solver'}</div>
                  </div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    <RichSummaryReport summary={analysisResult?.summary} className="text-[11px] leading-[1.18] text-black" />
                  </div>
                </div>
                {/* Shareable Link at Bottom */}
                <div className="grid grid-cols-2 mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold hover:text-blue-800" target="_blank" rel="noopener noreferrer" title="Click to open shareable link">
                    🔗 LINK
                  </a>
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none text-blue-500">{`${APP_NAME} V${APP_VERSION}`}</div>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 pl-2">
                  <PrintChartImageGrid charts={analysisResult?.charts} order={PRINT_CHART_ORDER} aspectByKey={PRINT_CHART_ASPECT} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
